import { useMemo } from 'react'
import { extractSnapshots, getActiveStrokes, type DrawingDataStructure } from '../utils/drawingUtils'

interface TimelineGraphProps {
  drawingData: DrawingDataStructure
}

export function TimelineGraph({ drawingData }: TimelineGraphProps) {
  const { totalDuration, strokeCount, snapshots } = useMemo(() => {
    let maxEndTime = 0
    let count = 0

    drawingData.forEach((page) => {
      getActiveStrokes(page).forEach((stroke) => {
        if (stroke.points.length === 0) return

        const strokeStartTime = (stroke.time_offset ?? 0) / 1_000_000
        const strokeDuration = stroke.points[stroke.points.length - 1].time_offset / 1_000_000
        const strokeEndTime = strokeStartTime + strokeDuration

        maxEndTime = Math.max(maxEndTime, strokeEndTime)
        count++
      })
    })

    // Extract snapshots (1 second gaps)
    const snapshotData = extractSnapshots(drawingData, 1000)

    return {
      totalDuration: maxEndTime, // 절대 시간
      strokeCount: count,
      snapshots: snapshotData
    }
  }, [drawingData])

  if (strokeCount === 0) {
    return (
      <div className="text-gray-500 italic text-sm">
        타임라인 데이터 없음
      </div>
    )
  }

  // Format time for display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    const ms = Math.floor((seconds % 1) * 1000)

    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`
    } else if (secs > 0) {
      return `${secs}s`
    } else {
      return `${ms}ms`
    }
  }

  // Calculate activity density (strokes per second)
  const { buckets, bucketSize, maxActivity } = useMemo(() => {
    const size = Math.max(0.5, totalDuration / 50) // 50 buckets max
    const bucketList: number[] = []
    const bucketCount = Math.ceil(totalDuration / size)

    for (let i = 0; i < bucketCount; i++) {
      bucketList.push(0)
    }

    drawingData.forEach(page => {
      getActiveStrokes(page).forEach(stroke => {
        if (stroke.points.length === 0) return
        const strokeStartTime = (stroke.time_offset ?? 0) / 1_000_000 // 절대 시간
        const bucketIndex = Math.floor(strokeStartTime / size)
        if (bucketIndex >= 0 && bucketIndex < bucketList.length) {
          bucketList[bucketIndex]++
        }
      })
    })

    const maxAct = Math.max(...bucketList, 1)
    return { buckets: bucketList, bucketSize: size, maxActivity: maxAct }
  }, [drawingData, totalDuration])

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-gray-500 font-medium">총 스트로크</div>
          <div className="text-2xl font-bold text-gray-900">{strokeCount}</div>
        </div>
        <div>
          <div className="text-gray-500 font-medium">총 시간</div>
          <div className="text-2xl font-bold text-gray-900">{formatTime(totalDuration)}</div>
        </div>
        <div>
          <div className="text-gray-500 font-medium">스냅샷 수</div>
          <div className="text-2xl font-bold text-gray-900">{snapshots.length}</div>
        </div>
      </div>

      {/* Activity Density Graph with Snapshot Markers */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="text-sm font-medium text-gray-700 mb-3">활동 밀도 및 스냅샷</div>

        <div className="relative" style={{ height: 160 }}>
          {/* Activity bars */}
          <div className="absolute inset-0 flex items-end gap-px pb-6">
            {buckets.map((count, idx) => {
              const heightPercent = (count / maxActivity) * 100
              const startTime = idx * bucketSize

              return (
                <div
                  key={idx}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 transition-colors rounded-t"
                  style={{ height: `${heightPercent}%` }}
                  title={`${formatTime(startTime)}: ${count} strokes`}
                />
              )
            })}
          </div>

          {/* Snapshot markers */}
          <div className="absolute bottom-0 left-0 right-0 h-6 border-t border-gray-300">
            {snapshots.map((snapshot, idx) => {
              const timeSeconds = snapshot.timeMs / 1000 // 절대 시간 (초)
              const xPercent = (timeSeconds / totalDuration) * 100

              return (
                <div
                  key={idx}
                  className="absolute bottom-0 group"
                  style={{ left: `${xPercent}%`, transform: 'translateX(-50%)' }}
                >
                  {/* Vertical line */}
                  <div className="w-px h-6 bg-red-500 group-hover:bg-red-600" />
                  {/* Marker dot */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 group-hover:bg-red-600 rounded-full border-2 border-white shadow" />
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    스냅샷 #{idx + 1}
                    <br />
                    {timeSeconds.toFixed(4)}s
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        <div className="mt-2 flex justify-between items-center text-xs">
          <span className="text-gray-500">0s</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-500 rounded" />
              <span className="text-gray-600">활동 밀도</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-gray-600">스냅샷</span>
            </div>
          </div>
          <span className="text-gray-500">{formatTime(totalDuration)}</span>
        </div>
      </div>
    </div>
  )
}
